import type { TPlayer } from 'video.js'
import type { XhrRequest, XhrResponse } from '|>/shared/types/errors'

import { Events } from '|>/shared/events'
import { getXhr } from '|>/shared/utils/vhs'

/** One VHS retry allowed; the next confirmed segment-404 retry is terminal (FP-4176). */
const MAX_RETRIES = 1

type Tech = {
  on: (event: string, handler: () => void) => void
  off: (event: string, handler: () => void) => void
  vhs?: { playlists?: { main?: { playlists?: unknown[] } } }
}

/**
 * Stops the VHS single-playlist endless `retryplaylist` loop on segment 404.
 * Does not change multi-rendition ABR policy.
 */
export function inject(player: TPlayer): TPlayer {
  let retryCount = 0
  let terminalFired = false
  let sawSegment404 = false
  let boundTech: Tech | null = null
  let xhrHookAttached = false

  const reset = () => {
    retryCount = 0
    terminalFired = false
    sawSegment404 = false
  }

  const xhrHook = (
    request: XhrRequest,
    _error: unknown,
    response: XhrResponse
  ) => {
    if (request.requestType !== 'segment') return
    if (response?.statusCode === 404) {
      sawSegment404 = true
      return
    }
    if (
      typeof response?.statusCode === 'number' &&
      response.statusCode >= 200 &&
      response.statusCode < 300
    ) {
      sawSegment404 = false
      retryCount = 0
    }
  }

  const onRetryPlaylist = () => {
    if (terminalFired || player.isDisposed?.()) return

    const tech = (
      player as unknown as { tech?: (s: boolean) => Tech | undefined }
    ).tech?.(true)
    const playlists = tech?.vhs?.playlists?.main?.playlists
    if (!sawSegment404 || !Array.isArray(playlists) || playlists.length !== 1) {
      return
    }

    retryCount += 1
    if (retryCount <= MAX_RETRIES) return

    terminalFired = true
    player.error(404)
  }

  const detachXhrHook = () => {
    if (!xhrHookAttached) return
    getXhr(player, 'playlist-retry-limit')?.offResponse(xhrHook)
    xhrHookAttached = false
  }

  const unbindTech = () => {
    boundTech?.off('retryplaylist', onRetryPlaylist)
    boundTech = null
  }

  const bindTech = () => {
    const tech = (
      player as unknown as { tech?: (s: boolean) => Tech | undefined }
    ).tech?.(true)
    if (!tech) return

    if (tech !== boundTech) {
      unbindTech()
      boundTech = tech
      tech.on('retryplaylist', onRetryPlaylist)
      reset()
    }

    const xhr = getXhr(player, 'playlist-retry-limit')
    if (!xhr) return
    xhr.offResponse(xhrHook)
    xhr.onResponse(xhrHook)
    xhrHookAttached = true
  }

  const onLoad = (_: unknown, src?: Events.Media.Load) => {
    if (src?.src) reset()
  }

  player.on(['xhr-hooks-ready', 'loadstart'], bindTech)
  player.on(Events.Media.Load, onLoad)
  player.on('playing', reset)
  player.on('dispose', () => {
    detachXhrHook()
    unbindTech()
    player.off(Events.Media.Load, onLoad)
    player.off('playing', reset)
    player.off('xhr-hooks-ready', bindTech)
    player.off('loadstart', bindTech)
    reset()
  })

  return player
}
